import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Container,
  Card,
  CardContent,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import axios from "axios";
import { backendUrl } from "../utils/urlHelper";

const ViewEnquiryList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const token = localStorage.getItem("token");
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : {};

  const enquiriesPerPage = 50;

  // ---------- New States for Status Change ----------
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // ---------- New States for Follow-Up ----------
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpNote, setFollowUpNote] = useState("");

  // ---------- Allowed Statuses Based on Role ----------
  const allowedStatusesForStaff = [
    "Pending follow-up",
    "Converted (Awaiting Admission)",
    "Unable to Convert (Not Interested)",
  ];
  const allowedStatusesForAdmin = [
    ...allowedStatusesForStaff,
    "Admitted", // Only admin/manager can set 'Admitted'
  ];

  // Helper function to get valid statuses for current user
  const getAllowedStatuses = () => {
    if (user.role === "admin" || user.role === "manager") {
      return allowedStatusesForAdmin;
    }
    return allowedStatusesForStaff; // staff
  };

  useEffect(() => {
    refetchEnquiries();
  }, [currentPage, enquiriesPerPage]);

  const fetchEnquiries = async (endpoint, page, limit) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${endpoint}?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: token },
        }
      );

      const { enquiries: fetchedEnquiries, pages } = response.data;
      setEnquiries(fetchedEnquiries || []);
      setTotalPages(pages || 1);
    } catch (error) {
      console.error("Failed to fetch enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and paginate enquiries
  const filteredEnquiries = enquiries.filter(
    (enquiry) =>
      enquiry.studentInfo.fullName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      enquiry.studentInfo.currentGrade
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Open Dialog with Selected Enquiry
  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDialog(true);
  };

  // Close Dialog
  const handleCloseDialog = () => {
    setSelectedEnquiry(null);
    setShowDialog(false);
  };

  const handleChangeStatusClick = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setNewStatus(enquiry.status); // Initialize with current status
    setStatusDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    try {
      if (!selectedEnquiry) return;
      await axios.patch(
        `${backendUrl}/api/enquiries/${selectedEnquiry._id}/status`,
        { newStatus },
        { headers: { Authorization: token } }
      );
      // Option 1: Re-fetch the entire list
      refetchEnquiries();
      // Option 2: Update local state if you want to avoid a full refresh:
      // const updatedEnquiries = enquiries.map((enq) =>
      //   enq._id === selectedEnquiry._id ? { ...enq, status: newStatus } : enq
      // );
      // setEnquiries(updatedEnquiries);

      setStatusDialogOpen(false);
      setSelectedEnquiry(null);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleCancelStatusUpdate = () => {
    setStatusDialogOpen(false);
    setSelectedEnquiry(null);
  };

  const handleAddFollowUpClick = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFollowUpNote("");
    setFollowUpDialogOpen(true);
  };

  const handleSaveFollowUp = async () => {
    try {
      if (!selectedEnquiry) return;
      await axios.post(
        `${backendUrl}/api/enquiries/${selectedEnquiry._id}/follow-up`,
        { note: followUpNote },
        {
          headers: { Authorization: token },
        }
      );
      refetchEnquiries();
      setFollowUpDialogOpen(false);
      setSelectedEnquiry(null);
    } catch (error) {
      console.error("Error adding follow-up:", error);
    }
  };

  const handleCancelFollowUp = () => {
    setFollowUpDialogOpen(false);
    setSelectedEnquiry(null);
  };

  // Helper to re-fetch enquiries after status/follow-up changes
  const refetchEnquiries = () => {
    if (user.role === "admin" || user.role === "manager") {
      fetchEnquiries(
        `${backendUrl}/api/enquiries`,
        currentPage,
        enquiriesPerPage
      );
    } else if (user.role === "staff") {
      fetchEnquiries(
        `${backendUrl}/api/teacher/enquiries`,
        currentPage,
        enquiriesPerPage
      );
    }
  };

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h5" sx={{ marginBottom: 3, textAlign: "center" }}>
        {user.role === "admin" ? "All Enquiries" : "Your Enquiries"}
      </Typography>

      {/* Search Bar */}
      <TextField
        label="Search by Student Name or Grade (Current Page Only)"
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 3 }}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Loader */}
      {loading && (
        <Typography textAlign="center" variant="body1">
          Loading enquiries...
        </Typography>
      )}

      {/* List of Enquiries */}
      {!loading && filteredEnquiries.length > 0
        ? filteredEnquiries.map((enquiry, index) => (
            <Card
              key={index}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                px: 1,
                py: 0,
                marginBottom: 2,
              }}
            >
              <CardContent>
                <Typography variant="h6">
                  {enquiry.studentInfo.fullName} (
                  {enquiry.studentInfo.currentGrade})
                </Typography>
                <Typography variant="body1" sx={{ marginY: 1 }}>
                  <strong>Status:</strong> {enquiry.status || "New"}
                </Typography>
                <Typography variant="body2" sx={{ marginY: 1 }}>
                  <strong>School:</strong>{" "}
                  {enquiry.studentInfo.currentSchool || "N/A"}
                </Typography>
                {/* <Typography variant="body2">
                  <strong>Reference Type:</strong>{" "}
                  {enquiry.marketing.referenceType}
                </Typography>
                <Typography variant="body2">
                  <strong>Lead Type:</strong> {enquiry.marketing.leadType}
                </Typography> */}

                {/* Show Current Status */}

                {/* Action Buttons */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: 1,
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    // sx={{ marginTop: 1 }}
                    onClick={() => handleViewDetails(enquiry)}
                  >
                    View Details
                  </Button>

                  {/* Change Status Button (staff, manager, admin) */}
                  {(user.role === "staff" ||
                    user.role === "admin" ||
                    user.role === "manager") && (
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={() => handleChangeStatusClick(enquiry)}
                    >
                      Change Status
                    </Button>
                  )}

                  {/* Add Follow-up Button (only relevant if 'Pending follow-up' or you want to allow for other statuses) */}
                  {(user.role === "staff" ||
                    user.role === "admin" ||
                    user.role === "manager") && (
                    <Button
                      variant="contained"
                      size="small"
                      color="warning"
                      onClick={() => handleAddFollowUpClick(enquiry)}
                    >
                      Add Follow-up
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))
        : !loading && (
            <Typography textAlign="center" variant="body1">
              No matching enquiries found on this page. Try checking other
              pages.
            </Typography>
          )}

      {/* Pagination */}
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handlePageChange}
        sx={{ marginTop: 3, display: "flex", justifyContent: "center" }}
      />

      {/* ========== Status Update Dialog ========== */}
      <Dialog open={statusDialogOpen} onClose={handleCancelStatusUpdate}>
        <DialogTitle>Update Enquiry Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              label="New Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {getAllowedStatuses().map((statusOption, i) => (
                <MenuItem key={i} value={statusOption}>
                  {statusOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStatusUpdate}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== Follow-Up Dialog ========== */}
      <Dialog open={followUpDialogOpen} onClose={handleCancelFollowUp}>
        <DialogTitle>Add Follow-up Note</DialogTitle>
        <DialogContent>
          <TextField
            label="Short Note"
            multiline
            rows={3}
            fullWidth
            value={followUpNote}
            onChange={(e) => setFollowUpNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelFollowUp}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFollowUp}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enquiry Details Dialog */}
      {selectedEnquiry && (
        <Dialog
          open={showDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Enquiry Details</DialogTitle>
          <DialogContent>
            <Typography variant="h6">Student Info</Typography>
            <Typography>
              <strong>Name:</strong> {selectedEnquiry.studentInfo.fullName}
            </Typography>
            <Typography>
              <strong>Grade:</strong> {selectedEnquiry.studentInfo.currentGrade}
            </Typography>
            <Typography>
              <strong>School:</strong>{" "}
              {selectedEnquiry.studentInfo.currentSchool || "N/A"}
            </Typography>

            <Typography variant="h6" mt={2}>
              Parents/Guardians
            </Typography>
            {selectedEnquiry.parents.map((parent, index) => (
              <Box key={index} sx={{ marginBottom: 2 }}>
                <Typography>
                  <strong>Relationship:</strong> {parent.relationship}
                </Typography>
                <Typography>
                  <strong>Name:</strong> {parent.name}
                </Typography>
                <Typography>
                  <strong>Mobile:</strong> {parent.mobile}
                </Typography>
                <Typography>
                  <strong>Occupation:</strong> {parent.occupation || "N/A"}
                </Typography>
              </Box>
            ))}

            {/* Sibling Info */}
            {selectedEnquiry.siblings.length > 0 && (
              <>
                <Typography variant="h6" mt={2}>
                  Sibling Details
                </Typography>
                {selectedEnquiry.siblings.map((sibling, index) => (
                  <Box key={index} sx={{ marginBottom: 2 }}>
                    <Typography>
                      <strong>Name:</strong> {sibling.name}
                    </Typography>
                    <Typography>
                      <strong>Grade:</strong> {sibling.grade}
                    </Typography>
                    <Typography>
                      <strong>School:</strong> {sibling.school || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </>
            )}

            <Typography variant="h6" mt={2}>
              Location
            </Typography>
            <Typography>
              <strong>Address:</strong> {selectedEnquiry.location.address}
            </Typography>
            <Typography>
              <strong>Village:</strong> {selectedEnquiry.location.village}
            </Typography>
            <Typography>
              <strong>GPS:</strong>{" "}
              {selectedEnquiry?.location?.coordinates?.latitude ? (
                <a
                  href={`https://maps.google.com/?q=${selectedEnquiry?.location?.coordinates?.latitude},${selectedEnquiry?.location?.coordinates?.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1a73e8",
                    textDecoration: "underline",
                    marginLeft: 2,
                  }}
                >
                  Open in Maps
                </a>
              ) : (
                "Not found"
              )}
            </Typography>

            <Typography variant="h6" mt={2}>
              Enquiry Info
            </Typography>
            <Typography>
              <strong>Reference Type:</strong>{" "}
              {selectedEnquiry.marketing.referenceType}
            </Typography>
            <Typography>
              <strong>Lead Type:</strong> {selectedEnquiry.marketing.leadType}
            </Typography>

            {/* Sibling Info */}
            {selectedEnquiry.teacherInfo?.teacherName && (
              <>
                <Typography variant="h6" mt={2}>
                  Teacher Info
                </Typography>
                <Typography>
                  <strong>Name:</strong>{" "}
                  {selectedEnquiry?.teacherInfo?.teacherName}
                </Typography>
                <Typography>
                  <strong>Branch:</strong>{" "}
                  {selectedEnquiry?.teacherInfo?.teacherBranch}
                </Typography>
              </>
            )}

            <Typography variant="h6" mt={2}>
              Other Info
            </Typography>
            <Typography>
              <strong>For AY:</strong> {selectedEnquiry?.academicYear}
            </Typography>
            <Typography>
              <strong>For Branch:</strong> {selectedEnquiry?.branch}
            </Typography>

            {selectedEnquiry.statusHistory &&
              selectedEnquiry.statusHistory.length > 0 && (
                <Divider sx={{ my: 2 }} />
              )}

            {/* ---------- Status History ---------- */}
            {selectedEnquiry.statusHistory &&
              selectedEnquiry.statusHistory.length > 0 && (
                <>
                  <Typography variant="h6" mt={2}>
                    Status Change History
                  </Typography>
                  {selectedEnquiry.statusHistory.map((history, idx) => (
                    <Box key={idx} sx={{ marginBottom: 2 }}>
                      <Typography>
                        <strong>Changed By:</strong>{" "}
                        {history.changedByName || "N/A"}
                      </Typography>
                      <Typography>
                        <strong>When:</strong>{" "}
                        {new Date(history.changedAt).toLocaleString()}
                      </Typography>
                      <Typography>
                        <strong>From:</strong> {history.fromStatus}
                      </Typography>
                      <Typography>
                        <strong>To:</strong> {history.toStatus}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

            {selectedEnquiry.followUps &&
              selectedEnquiry.followUps.length > 0 && (
                <Divider sx={{ my: 2 }} />
              )}

            {/* ---------- Follow-Up History ---------- */}
            {selectedEnquiry.followUps &&
              selectedEnquiry.followUps.length > 0 && (
                <>
                  <Typography variant="h6" mt={2}>
                    Follow-up History
                  </Typography>
                  {selectedEnquiry.followUps.map((fu, index) => (
                    <Box key={index} sx={{ marginBottom: 2 }}>
                      <Typography>
                        <strong>By:</strong> {fu.followUpByName || "Unknown"}
                      </Typography>
                      <Typography>
                        <strong>Date:</strong>{" "}
                        {new Date(fu.createdAt).toLocaleString()}
                      </Typography>
                      <Typography>
                        <strong>Note:</strong> {fu.note}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default ViewEnquiryList;
